import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import makeRequest from "../../makeRequest";

function getInitials(name) {
  if (!name) return "P";
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const AVATAR_COLORS = [
  ["#1B74BB", "#e8f3fc"],
  ["#076cec", "#ddeeff"],
  ["#0e9976", "#d3f5ee"],
  ["#7c3aed", "#ede9fe"],
  ["#dc6803", "#fef3c7"],
];

function TestimonialCard({ item, colorIdx }) {
  const [bg, light] = AVATAR_COLORS[colorIdx % AVATAR_COLORS.length];
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "36px 32px 28px",
        boxShadow: "0 4px 32px rgba(7,108,236,0.08)",
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid rgba(7,108,236,0.07)",
      }}
    >
      {/* quote icon */}
      <span
        style={{
          position: "absolute",
          top: 22,
          right: 28,
          fontSize: 72,
          lineHeight: 1,
          color: "rgba(7,108,236,0.07)",
          fontFamily: "Georgia, serif",
          userSelect: "none",
          pointerEvents: "none",
        }}
      >
        &#8220;
      </span>

      {/* stars */}
      <div style={{ marginBottom: 16, display: "flex", gap: 3 }}>
        {[1, 2, 3, 4, 5].map((s) => (
          <i key={s} className="fa-solid fa-star" style={{ color: "#f59e0b", fontSize: 13 }} />
        ))}
      </div>

      {/* message */}
      <p
        style={{
          fontSize: "0.95rem",
          color: "#374151",
          lineHeight: 1.75,
          flex: 1,
          margin: 0,
          marginBottom: 28,
        }}
      >
        {item.message}
      </p>

      {/* divider */}
      <div
        style={{
          height: 1,
          background: "linear-gradient(90deg,rgba(7,108,236,0.15),transparent)",
          marginBottom: 20,
        }}
      />

      {/* author */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            background: light,
            color: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "0.95rem",
            flexShrink: 0,
            border: `2px solid ${bg}22`,
          }}
        >
          {getInitials(item.name)}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>{item.name}</div>
          <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2 }}>{item?.location}</div>
        </div>
      </div>
    </div>
  );
}

const Testimonial = () => {
  const [testimonials, setTestimonials] = useState([]);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    makeRequest({ route: "/website/testimonial/public", method: "get" })
      .then((data) => setTestimonials(data?.data || []))
      .catch(() => {});
  }, []);

  if (testimonials.length === 0) return null;

  return (
    <div
      id="it-testimonial"
      style={{
        background: "linear-gradient(135deg, #f0f6ff 0%, #f8fafc 60%, #eef9f6 100%)",
        padding: "90px 0 100px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* decorative blobs */}
      <div
        style={{
          position: "absolute",
          top: -80,
          left: -80,
          width: 320,
          height: 320,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(7,108,236,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -60,
          right: -60,
          width: 260,
          height: 260,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(14,153,118,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div className="container" style={{ position: "relative" }}>
        {/* header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span
            style={{
              display: "inline-block",
              background: "rgba(7,108,236,0.08)",
              color: "#076cec",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderRadius: 20,
              padding: "5px 18px",
              marginBottom: 16,
            }}
          >
            Patient Stories
          </span>
          <h2
            style={{
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.2,
              margin: "0 auto",
              maxWidth: 520,
            }}
          >
            What Our Patients Say
          </h2>
          <p
            style={{
              color: "#6b7280",
              fontSize: "0.95rem",
              marginTop: 14,
              maxWidth: 460,
              margin: "14px auto 0",
            }}
          >
            Real experiences from patients who trusted us with their care.
          </p>
        </div>

        {/* swiper */}
        <div style={{ position: "relative" }}>
          <Swiper
            modules={[Navigation, Autoplay, Pagination]}
            speed={700}
            loop={testimonials.length > 2}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            slidesPerView={1}
            spaceBetween={24}
            pagination={{ clickable: true, el: ".mh-test-pagination" }}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1200: { slidesPerView: 3 },
            }}
            style={{ paddingBottom: 52 }}
          >
            {testimonials.map((item, i) => (
              <SwiperSlide key={item.id} style={{ height: "auto" }}>
                <TestimonialCard item={item} colorIdx={i} />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* pagination dots */}
          <div className="mh-test-pagination" style={{ textAlign: "center", marginTop: -20 }} />

          {/* nav buttons */}
          <button
            ref={prevRef}
            style={{
              position: "absolute",
              bottom: 0,
              left: "calc(50% - 52px)",
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "2px solid #076cec",
              background: "#fff",
              color: "#076cec",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              fontSize: 14,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#076cec";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#076cec";
            }}
          >
            <i className="fa-regular fa-angle-left" />
          </button>
          <button
            ref={nextRef}
            style={{
              position: "absolute",
              bottom: 0,
              left: "calc(50% + 12px)",
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "2px solid #076cec",
              background: "#fff",
              color: "#076cec",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              fontSize: 14,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#076cec";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.color = "#076cec";
            }}
          >
            <i className="fa-regular fa-angle-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Testimonial;
